import * as React from "react"
import { ChevronLeft, ChevronRight, Search } from "lucide-react"
import {
    format,
    addMonths,
    subMonths,
    startOfMonth,
    endOfMonth,
    isSameDay,
    isWithinInterval,
    eachDayOfInterval,
    isSameMonth,
} from "date-fns"

interface DateRange {
    from: Date | undefined
    to: Date | undefined
}

interface DateRangePickerProps {
    isOpen: boolean
    onClose: () => void
    onUpdate: (range: DateRange) => void
    initialRange?: DateRange
}

export function DateRangePicker({ isOpen, onClose, onUpdate, initialRange }: DateRangePickerProps) {
    const currentDate = new Date()
    const [range, setRange] = React.useState<DateRange>(
        initialRange || { from: undefined, to: undefined },
    )
    const [leftMonth, setLeftMonth] = React.useState(() => {
        // If initialRange has a from date, use that month, otherwise use current month
        if (initialRange?.from && !isNaN(initialRange.from.getTime())) {
            return new Date(initialRange.from.getFullYear(), initialRange.from.getMonth())
        }
        return new Date(currentDate.getFullYear(), currentDate.getMonth())
    })
    const [showYearPicker, setShowYearPicker] = React.useState<{ month: Date; isLeft: boolean } | null>(null)
    const [yearSearchQuery, setYearSearchQuery] = React.useState("")
    const yearSearchInputRef = React.useRef<HTMLInputElement>(null)
    const rightMonth = addMonths(leftMonth, 1)

    // Update range when initialRange changes
    React.useEffect(() => {
        if (initialRange) {
            const validFrom = initialRange.from && !isNaN(initialRange.from.getTime()) ? initialRange.from : undefined
            const validTo = initialRange.to && !isNaN(initialRange.to.getTime()) ? initialRange.to : undefined
            setRange({ from: validFrom, to: validTo })
            if (validFrom) {
                setLeftMonth(new Date(validFrom.getFullYear(), validFrom.getMonth()))
            } else {
                // If no from date, show current month
                const now = new Date()
                setLeftMonth(new Date(now.getFullYear(), now.getMonth()))
            }
        } else {
            // If initialRange is undefined, reset to current month
            const now = new Date()
            setLeftMonth(new Date(now.getFullYear(), now.getMonth()))
        }
    }, [initialRange])

    // When picker opens, ensure it shows current month if no range is set
    React.useEffect(() => {
        if (isOpen && !range.from && !range.to) {
            const now = new Date()
            setLeftMonth(new Date(now.getFullYear(), now.getMonth()))
        }
    }, [isOpen, range.from, range.to])

    // Close year picker when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showYearPicker && !(event.target as Element).closest('.year-picker-container')) {
                setShowYearPicker(null)
                setYearSearchQuery("")
            }
        }
        if (showYearPicker) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showYearPicker])

    React.useEffect(() => {
        if (!showYearPicker) return

        const frameId = window.requestAnimationFrame(() => {
            yearSearchInputRef.current?.focus()
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [showYearPicker])

    if (!isOpen) return null


    const handleDayClick = (day: Date) => {
        // Check if the selected day is in the currently visible months
        const isInLeftMonth = isSameMonth(day, leftMonth)
        const isInRightMonth = isSameMonth(day, rightMonth)
        
        // If the day is not in either visible month, navigate to that month
        if (!isInLeftMonth && !isInRightMonth) {
            setLeftMonth(new Date(day.getFullYear(), day.getMonth()))
        }
        
        if (!range.from || (range.from && range.to)) {
            setRange({ from: day, to: undefined })
        } else if (day < range.from) {
            setRange({ from: day, to: range.from })
        } else {
            setRange({ ...range, to: day })
        }
    }

    const handleYearSelect = (year: number, isLeft: boolean) => {
        if (isLeft) {
            setLeftMonth(new Date(year, leftMonth.getMonth()))
        } else {
            // For right calendar, update leftMonth to maintain the two-month view
            const newRightMonth = new Date(year, rightMonth.getMonth())
            setLeftMonth(subMonths(newRightMonth, 1))
        }
        setShowYearPicker(null)
        setYearSearchQuery("")
    }

    const renderYearPicker = (month: Date, isLeft: boolean) => {
        const currentYear = month.getFullYear()
        const startYear = currentYear - 10
        const endYear = currentYear + 10
        const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i)
        const query = yearSearchQuery.trim()
        const filteredYears = query
            ? years.filter((year) => String(year).includes(query))
            : years

        return (
            <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden w-36">
                <div
                    className="sticky top-0 z-10 border-b border-gray-200 bg-white p-2"
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                        <input
                            ref={yearSearchInputRef}
                            type="text"
                            value={yearSearchQuery}
                            onChange={(event) => setYearSearchQuery(event.target.value)}
                            placeholder="Search year"
                            className="w-full rounded-md border border-gray-200 bg-[#F9F9F9] py-1.5 pl-7 pr-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-lime-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                            aria-label="Search year"
                        />
                    </div>
                </div>
                <div className="max-h-52 overflow-y-auto p-2">
                    {filteredYears.length === 0 ? (
                        <p className="px-2 py-2 text-sm text-gray-500">No years found</p>
                    ) : (
                        filteredYears.map((year) => (
                            <button
                                key={year}
                                onClick={() => handleYearSelect(year, isLeft)}
                                className={`w-full text-left px-3 py-2 text-sm rounded-md hover:bg-gray-100 transition-colors ${
                                    year === currentYear ? "bg-[#ABD405] text-white font-bold" : "text-black"
                                }`}
                            >
                                {year}
                            </button>
                        ))
                    )}
                </div>
            </div>
        )
    }

    const renderCalendar = (month: Date, isLeftCalendar: boolean) => {
        const monthStart = startOfMonth(month)
        const monthEnd = endOfMonth(month)
        
        // Get all days of the current month only
        const monthDays = eachDayOfInterval({
            start: monthStart,
            end: monthEnd,
        })
        
        // Calculate day of week for the first day (Monday = 0, Sunday = 6)
        const firstDayOfWeek = (monthStart.getDay() + 6) % 7
        // Calculate how many empty cells needed after the last day
        const lastDayOfWeek = (monthEnd.getDay() + 6) % 7
        const emptyCellsAfter = 6 - lastDayOfWeek

        const isYearPickerOpen = showYearPicker?.isLeft === isLeftCalendar && showYearPicker?.month === month

        return (
            <div className="flex flex-col gap-4 relative">
                <div className="flex items-center justify-between px-2">
                    <button
                        onClick={() => setLeftMonth(subMonths(leftMonth, 1))}
                        className="rounded-md border border-gray-200 p-2 bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                    >
                        <ChevronLeft strokeWidth={2.5} className="h-4 w-4 text-slate-700" />
                    </button>
                    <div className="relative year-picker-container">
                        <button
                            onClick={() => {
                                if (isYearPickerOpen) {
                                    setShowYearPicker(null)
                                    setYearSearchQuery("")
                                } else {
                                    setYearSearchQuery("")
                                    setShowYearPicker({ month, isLeft: isLeftCalendar })
                                }
                            }}
                            className="text-sm font-bold hover:text-[#ABD405] transition-colors cursor-pointer"
                        >
                            {format(month, "MMM, yyyy")}
                        </button>
                        {isYearPickerOpen && renderYearPicker(month, isLeftCalendar)}
                    </div>
                    <button
                        onClick={() => setLeftMonth(addMonths(leftMonth, 1))}
                        className="rounded-md border border-gray-200 p-2 bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                    >
                        <ChevronRight strokeWidth={2.5} className="h-4 w-4 text-slate-700" />
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-5">
                    {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                        <div key={d} className="text-center text-sm font-medium text-[#1F2937]/30">
                            {d}
                        </div>
                    ))}
                    {/* Add empty cells before the first day of the month (for right calendar - to avoid showing previous month) */}
                    {!isLeftCalendar && firstDayOfWeek > 0 && (
                        Array.from({ length: firstDayOfWeek }, (_, i) => (
                            <div key={`empty-before-${i}`} className="h-8 w-10" />
                        ))
                    )}
                    {/* For left calendar, also add empty cells if month doesn't start on Monday */}
                    {isLeftCalendar && firstDayOfWeek > 0 && (
                        Array.from({ length: firstDayOfWeek }, (_, i) => (
                            <div key={`empty-before-left-${i}`} className="h-8 w-10" />
                        ))
                    )}
                    {monthDays.map((day, idx) => {
                        const isAllTime = !range.from && !range.to
                        const isSelected = (range.from && isSameDay(day, range.from)) || (range.to && isSameDay(day, range.to))
                        const isInRange = range.from && range.to && isWithinInterval(day, { start: range.from, end: range.to })
                        const isInAllTimeRange = isAllTime

                        return (
                            <button
                                key={idx}
                                onClick={() => handleDayClick(day)}
                                className={`h-8 w-10 text-xs p-2 text-right font-medium transition-colors rounded-md ${isSelected
                                    ? "bg-[#ABD405] text-white font-bold"
                                    : isInRange || isInAllTimeRange
                                        ? "bg-[#EBF0FF] border-2 border-gray-200 text-black"
                                        : "bg-[#F3F4F6]/30 border-2 border-gray-100 text-black hover:bg-gray-100"
                                    }`}
                            >
                                {format(day, "d")}
                            </button>
                        )
                    })}
                    {/* Add empty cells after the last day of the month (only for left calendar - to avoid showing next month) */}
                    {isLeftCalendar && emptyCellsAfter > 0 && (
                        Array.from({ length: emptyCellsAfter }, (_, i) => (
                            <div key={`empty-after-${i}`} className="h-8 w-10" />
                        ))
                    )}
                </div>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="flex w-full max-w-4xl overflow-hidden rounded-md bg-white shadow-2xl">
                
                {/* Main Content */}
                <div className="flex-1 p-6">
                    <div className="mb-8 flex gap-6">
                        <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-black mb-3">Start Date</label>
                            <div className="rounded-md border font-normal p-2 text-sm text-[#9CA3AF] bg-white shadow">
                                {range.from && !isNaN(range.from.getTime()) ? format(range.from, "MM/dd/yyyy") : "mm/dd/yy"}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1 flex-1">
                            <label className="text-sm font-medium text-black mb-3">End Date</label>
                            <div className="rounded-md border font-normal p-2 text-sm text-[#9CA3AF] bg-white shadow">
                                {range.to && !isNaN(range.to.getTime()) ? format(range.to, "MM/dd/yyyy") : "mm/dd/yy"}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between">
                        {renderCalendar(leftMonth, true)}
                        {renderCalendar(rightMonth, false)}
                    </div>

                    <div className="mt-8 flex items-center justify-end pt-5">
                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="rounded-md border px-6 py-2 text-sm font-semibold bg-gradient-to-t from-white via-white via-20% to-gray-100 transition-all shadow-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onUpdate(range)}
                                className="btn-cta rounded-md px-6 py-2 text-sm font-semibold"
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
